import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin, Result } from "antd";
import dayjs from "dayjs";
import API from "Util/Axios";
import PrintGate from "./PrintGate";
import CronogramaTimeline, { CronogramaLeyenda } from "components/Order/Detail/components/Cronograma/CronogramaTimeline";
import { dateOnly, estadoHitoMeta } from "components/Order/Detail/components/Cronograma/cronogramaUtils";

// /print/cronograma/:id — visited by jag-backend's Puppeteer PDF route
// (GET /order/:id/cronograma/pdf), which waits for `data-print-ready` (set only
// after a successful load). Data:
// `order/:id/cronograma/print-data` (not blocked by the order state) + `config-site`,
// authenticated by the `?token=` print token (see PrintGate).
const fmt = (d) => (d ? dayjs(dateOnly(d)).format("DD/MM/YYYY") : "—");

const th = { textAlign: "left", padding: "6px 8px", background: "#f5f5f5", border: "1px solid #d9d9d9", fontSize: 12 };
const td = { padding: "6px 8px", border: "1px solid #d9d9d9", fontSize: 12 };

const Dato = ({ label, children }) => (
  <div style={{ minWidth: 150 }}>
    <div style={{ fontSize: 10, textTransform: "uppercase", color: "#8c8c8c", letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600 }}>{children || "—"}</div>
  </div>
);

const CronogramaPrintContent = ({ id }) => {
  const [state, setState] = useState({ loading: true, error: false, order: null, empresa: {} });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: false, order: null, empresa: {} });
    Promise.all([API.get(`order/${id}/cronograma/print-data`), API.get("config-site").catch(() => ({ data: {} }))])
      .then(([{ data: detail }, { data: config }]) => {
        if (!cancelled) setState({ loading: false, error: false, order: detail?.order || {}, empresa: config?.empresa || {} });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, order: null, empresa: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.error) return <Result status="error" title="No se pudo cargar el cronograma" />;
  if (state.loading || !state.order) {
    return (
      <div style={{ width: "100%", padding: 80, textAlign: "center" }}>
        <Spin spinning tip="Cargando..." />
      </div>
    );
  }

  const { order, empresa } = state;
  const hitos = order.cronograma?.hitos || [];
  // The client only receives the INITIAL schedule (owner decision 2026-10-27):
  // `fechaVigente` and the reprogramaciones are internal and never rendered here.
  const { fechaComprometida } = order.entrega || {};
  const cotizacion = order.cotizacion;

  return (
    <div style={{ padding: "8mm 10mm", fontFamily: "Arial, Helvetica, sans-serif", color: "#262626" }} data-print-ready="true">
      <style>{`@page { size: A4 landscape; margin: 8mm; }`}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #0c4a8a", paddingBottom: 8, marginBottom: 12 }}>
        <img src={`${process.env.PUBLIC_URL || ""}/imagenes/logo_color_pdf.jpg`} alt="JAG" style={{ height: 44 }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0c4a8a" }}>Cronograma de ejecución</div>
          <div style={{ fontSize: 12, color: "#595959" }}>{empresa?.nombre || "JAG Ingeniería y Refrigeración S.A.S."}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        <Dato label="Cliente">{order.customer?.name}</Dato>
        <Dato label="Sede">{order.subsidiary?.name}</Dato>
        <Dato label="Orden">{order.code ? `#${order.code}` : ""}</Dato>
        <Dato label="Cotización">{cotizacion?.code ? `#${cotizacion.code}` : ""}</Dato>
        <Dato label="Fecha de entrega">{fmt(fechaComprometida)}</Dato>
      </div>

      <CronogramaTimeline hitos={hitos} fechaComprometida={fechaComprometida} showToday={false} ocultarInterno />
      <CronogramaLeyenda />

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 30 }}>#</th>
            <th style={th}>Hito</th>
            <th style={th}>Inicio</th>
            <th style={th}>Fin</th>
            <th style={th}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {hitos.map((h, i) => (
            <tr key={h._id || i}>
              <td style={td}>{i + 1}</td>
              <td style={td}>{h.nombre}</td>
              <td style={td}>{fmt(h.inicio)}</td>
              <td style={td}>{fmt(h.fin)}</td>
              <td style={td}>{estadoHitoMeta(h.estado).label}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 10, color: "#8c8c8c" }}>
        Generado el {dayjs().format("DD/MM/YYYY")}. Las fechas pueden ajustarse según el avance de la obra.
      </div>
    </div>
  );
};

const CronogramaPrintPage = () => {
  const { id } = useParams();
  return (
    <PrintGate>
      <CronogramaPrintContent id={id} />
    </PrintGate>
  );
};

export default CronogramaPrintPage;
